/**
 * MonitorDetailPage.tsx — /app/monitors/:id — single-monitor drill-down
 *
 * Purpose:    Response-time chart, 90-day uptime history bars, recent-checks
 *             log, SSL-expiry countdown, and edit/pause/resume/delete actions
 *             wired to the gateway's PATCH/DELETE monitor routes.
 * Inputs:     monitorsApi from @/lib/saas-client (list + checks + setPaused +
 *             update + remove); route param :id
 * Outputs:    Monitor detail UI; charts are dependency-free inline SVG
 * Constraints: Dark-first; WCAG AA. GET /v1/monitors/{id}/checks is rolling
 *             out — a 404/405/501 renders <ComingOnline/>, never an error.
 * SOT:        nsentry SaaS completion spec § G-WEB (monitor detail)
 */
import { useEffect, useState, type FormEvent } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { AsyncScreen, type AsyncState } from '@/components/AsyncScreen'
import { ComingOnline } from '@/components/ComingOnline'
import { monitorsApi, type Monitor, type MonitorCheck } from '@/lib/saas-client'
import { isComingOnline, type SentryError } from '@/lib/sentry-error'

const STATUS_STYLES: Record<Monitor['status'], string> = {
  up: 'bg-green-50 dark:bg-green-900/50 text-green-700 dark:text-green-300',
  down: 'bg-red-50 dark:bg-red-900/50 text-red-700 dark:text-red-300',
  degraded: 'bg-yellow-50 dark:bg-yellow-900/50 text-yellow-700 dark:text-yellow-300',
  paused: 'bg-brand-surface-2 text-brand-muted',
  pending: 'bg-brand-surface-2 text-brand-muted',
}

// ─── Chart helpers (pure; inline SVG, no chart dependency) ───────────────────

/** Latency polyline points for the response-time chart (oldest → newest). */
export function latencyPoints(checks: MonitorCheck[], width: number, height: number): string {
  const series = [...checks]
    .sort((a, b) => a.checkedAt.localeCompare(b.checkedAt))
    .map((c) => c.latencyMs)
    .filter((v): v is number => v != null)
  if (series.length < 2) return ''
  const max = Math.max(...series, 1)
  const stepX = width / (series.length - 1)
  return series
    .map((v, i) => `${(i * stepX).toFixed(1)},${(height - (v / max) * (height - 4) - 2).toFixed(1)}`)
    .join(' ')
}

export interface DayBucket {
  date: string
  upPct: number | null
}

/** Bucket checks into the trailing `days` calendar days (oldest first). */
export function uptimeDays(checks: MonitorCheck[], days: number, now = new Date()): DayBucket[] {
  const byDay = new Map<string, { up: number; total: number }>()
  for (const c of checks) {
    const day = c.checkedAt.slice(0, 10)
    const bucket = byDay.get(day) ?? { up: 0, total: 0 }
    bucket.total += 1
    if (c.status === 'up') bucket.up += 1
    byDay.set(day, bucket)
  }
  const out: DayBucket[] = []
  for (let i = days - 1; i >= 0; i--) {
    const date = new Date(now.getTime() - i * 86_400_000).toISOString().slice(0, 10)
    const bucket = byDay.get(date)
    out.push({ date, upPct: bucket ? Math.round((bucket.up / bucket.total) * 1000) / 10 : null })
  }
  return out
}

/** Days until an ISO date; negative when past. */
export function daysUntil(iso: string, now = new Date()): number {
  return Math.ceil((new Date(iso).getTime() - now.getTime()) / 86_400_000)
}

function dayBarClass(upPct: number | null): string {
  if (upPct == null) return 'bg-brand-surface-2'
  if (upPct >= 99.5) return 'bg-green-500'
  if (upPct >= 95) return 'bg-yellow-500'
  return 'bg-red-500'
}

// ─── Page ────────────────────────────────────────────────────────────────────

export function MonitorDetailPage() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const [state, setState] = useState<AsyncState>('loading')
  const [monitor, setMonitor] = useState<Monitor | null>(null)
  const [checks, setChecks] = useState<MonitorCheck[] | null>(null)
  const [checksError, setChecksError] = useState<SentryError | undefined>()
  const [error, setError] = useState<SentryError | undefined>()
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState({ name: '', url: '', intervalMinutes: 5 })
  const [formError, setFormError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function load() {
    setState('loading')
    // The gateway lists monitors (no documented GET /v1/monitors/{id}); a
    // tenant's list is small, so find the record client-side.
    const [listRes, checksRes] = await Promise.all([monitorsApi.list(), monitorsApi.checks(id, 500)])
    if (!listRes.ok) {
      setError(listRes.error)
      setState('error')
      return
    }
    const found = listRes.value.find((m) => m.id === id) ?? null
    if (!found) {
      setError({ type: 'config_error', message: 'Monitor not found — it may have been deleted.' })
      setState('error')
      return
    }
    setMonitor(found)
    setForm({ name: found.name, url: found.url, intervalMinutes: found.intervalSeconds / 60 })
    if (checksRes.ok) {
      setChecks(checksRes.value)
      setChecksError(undefined)
    } else {
      setChecks(null)
      setChecksError(checksRes.error)
    }
    setState('success')
  }

  useEffect(() => {
    void load()
  }, [id])

  async function handleSave(e: FormEvent) {
    e.preventDefault()
    setFormError(null)
    if (!form.url.startsWith('http://') && !form.url.startsWith('https://')) {
      setFormError('URL must start with http:// or https://')
      return
    }
    const result = await monitorsApi.update(id, {
      name: form.name,
      url: form.url,
      intervalSeconds: form.intervalMinutes * 60,
    })
    if (!result.ok) {
      setFormError(result.error.message)
      return
    }
    setEditing(false)
    await load()
  }

  async function handlePauseToggle() {
    if (!monitor) return
    setBusy(true)
    await monitorsApi.setPaused(id, monitor.status !== 'paused')
    setBusy(false)
    await load()
  }

  async function handleDelete() {
    if (!confirm('Delete this monitor? Its history goes with it.')) return
    await monitorsApi.remove(id)
    navigate('/app/monitors')
  }

  const sslDays = monitor?.sslExpiresAt ? daysUntil(monitor.sslExpiresAt) : null
  const recent = checks ? [...checks].sort((a, b) => b.checkedAt.localeCompare(a.checkedAt)).slice(0, 30) : []

  return (
    <div className="p-6 lg:p-8">
      <Link to="/app/monitors" className="text-sm text-sky-700 dark:text-sky-400 hover:text-sky-700 dark:text-sky-300">← Monitors</Link>

      <AsyncScreen state={state} error={error} onRetry={load}>
        {monitor && (
          <>
            <div className="mb-6 mt-3 flex flex-wrap items-center justify-between gap-4">
              <div>
                <h1 className="flex items-center gap-3 text-xl font-semibold text-brand-ink">
                  {monitor.name}
                  <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLES[monitor.status]}`}>{monitor.status}</span>
                </h1>
                <p className="mt-1 text-sm text-brand-muted">
                  {monitor.url} · every {monitor.intervalSeconds / 60} min
                  {monitor.latencyMs != null && ` · ${monitor.latencyMs}ms`}
                </p>
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={() => setEditing((v) => !v)} className="rounded-md border border-brand-card px-3 py-1.5 text-sm font-medium text-brand-secondary hover:bg-brand-surface-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-400">
                  {editing ? 'Close editor' : 'Edit'}
                </button>
                <button type="button" onClick={handlePauseToggle} disabled={busy} className="rounded-md border border-brand-card px-3 py-1.5 text-sm font-medium text-brand-secondary hover:bg-brand-surface-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-400 disabled:opacity-50">
                  {monitor.status === 'paused' ? 'Resume' : 'Pause'}
                </button>
                <button type="button" onClick={handleDelete} className="rounded-md border border-red-900 px-3 py-1.5 text-sm font-medium text-red-700 dark:text-red-400 hover:bg-red-50 dark:bg-red-950/40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-red-400">
                  Delete
                </button>
              </div>
            </div>

            {editing && (
              <form onSubmit={handleSave} className="mb-6 space-y-4 rounded-xl border border-brand-subtle bg-brand-surface p-6" aria-label="Edit monitor">
                {formError && <p className="text-sm text-red-700 dark:text-red-300" role="alert">{formError}</p>}
                <div className="grid gap-4 sm:grid-cols-3">
                  <div>
                    <label htmlFor="md-name" className="mb-1 block text-sm font-medium text-brand-secondary">Name</label>
                    <input id="md-name" required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="w-full rounded-lg border border-brand-card bg-brand-surface-2 px-3 py-2 text-sm text-brand-ink focus-visible:ring-2 focus-visible:ring-sky-500 focus:outline-none" />
                  </div>
                  <div>
                    <label htmlFor="md-url" className="mb-1 block text-sm font-medium text-brand-secondary">URL</label>
                    <input id="md-url" required type="url" value={form.url} onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))} className="w-full rounded-lg border border-brand-card bg-brand-surface-2 px-3 py-2 text-sm text-brand-ink focus-visible:ring-2 focus-visible:ring-sky-500 focus:outline-none" />
                  </div>
                  <div>
                    <label htmlFor="md-interval" className="mb-1 block text-sm font-medium text-brand-secondary">Interval (minutes)</label>
                    <input id="md-interval" required type="number" min={1} value={form.intervalMinutes} onChange={(e) => setForm((f) => ({ ...f, intervalMinutes: Number(e.target.value) }))} className="w-full rounded-lg border border-brand-card bg-brand-surface-2 px-3 py-2 text-sm text-brand-ink focus-visible:ring-2 focus-visible:ring-sky-500 focus:outline-none" />
                  </div>
                </div>
                <button type="submit" className="rounded-md bg-sky-700 px-4 py-2 text-sm font-medium text-white hover:bg-sky-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-400">Save changes</button>
              </form>
            )}

            <section className="mb-6 rounded-xl border border-brand-subtle bg-brand-surface p-5" aria-label="SSL certificate">
              <h2 className="mb-1 text-sm font-semibold uppercase tracking-wide text-brand-muted">SSL certificate</h2>
              {sslDays == null ? (
                <p className="text-sm text-brand-muted">Expiry tracking comes online after the next certificate scan.</p>
              ) : (
                <p className={`text-lg font-semibold ${sslDays <= 7 ? 'text-red-700 dark:text-red-300' : sslDays <= 21 ? 'text-yellow-700 dark:text-yellow-300' : 'text-green-700 dark:text-green-300'}`}>
                  {sslDays < 0 ? `Expired ${-sslDays} day${sslDays === -1 ? '' : 's'} ago` : `Expires in ${sslDays} day${sslDays === 1 ? '' : 's'}`}
                  <span className="ml-2 text-sm font-normal text-brand-muted">({new Date(monitor.sslExpiresAt as string).toLocaleDateString()})</span>
                </p>
              )}
            </section>

            {checksError && isComingOnline(checksError) ? (
              <ComingOnline feature="Per-check history" description="Response-time charts, 90-day uptime bars, and the check log appear here." />
            ) : checksError ? (
              <p className="rounded-xl border border-yellow-200 dark:border-yellow-800/60 bg-yellow-50 dark:bg-yellow-950/30 p-4 text-sm text-yellow-700 dark:text-yellow-300" role="alert">
                Check history unavailable right now: {checksError.message}
              </p>
            ) : checks && (
              <>
                <section className="mb-6 rounded-xl border border-brand-subtle bg-brand-surface p-5" aria-label="Response time">
                  <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-brand-muted">Response time (recent checks)</h2>
                  {latencyPoints(checks, 600, 120) ? (
                    <svg viewBox="0 0 600 120" className="h-32 w-full" role="img" aria-label="Response-time chart">
                      <polyline points={latencyPoints(checks, 600, 120)} fill="none" stroke="#5b9dff" strokeWidth="2" strokeLinejoin="round" />
                    </svg>
                  ) : (
                    <p className="text-sm text-brand-muted">Not enough probe data yet — the chart appears after a few checks.</p>
                  )}
                </section>

                <section className="mb-6 rounded-xl border border-brand-subtle bg-brand-surface p-5" aria-label="90-day uptime">
                  <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-brand-muted">Uptime — last 90 days</h2>
                  <div className="flex h-10 items-end gap-px" role="img" aria-label="Daily uptime bars for the last 90 days">
                    {uptimeDays(checks, 90).map((d) => (
                      <div key={d.date} className={`h-full min-w-0 flex-1 rounded-sm ${dayBarClass(d.upPct)}`} title={`${d.date}: ${d.upPct == null ? 'no data' : `${d.upPct}% up`}`} />
                    ))}
                  </div>
                  <p className="mt-2 text-xs text-brand-muted">Green ≥99.5% · yellow ≥95% · red &lt;95% · gray = no data</p>
                </section>

                <section className="overflow-x-auto rounded-xl border border-brand-subtle" aria-label="Recent checks">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-brand-surface text-brand-muted">
                      <tr>
                        <th scope="col" className="px-4 py-3 font-medium">Checked at</th>
                        <th scope="col" className="px-4 py-3 font-medium">Result</th>
                        <th scope="col" className="px-4 py-3 font-medium">Latency</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-800 bg-brand-bg">
                      {recent.map((c) => (
                        <tr key={c.checkedAt}>
                          <td className="px-4 py-2.5 text-brand-muted">{new Date(c.checkedAt).toLocaleString()}</td>
                          <td className="px-4 py-2.5">
                            <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${c.status === 'up' ? 'bg-green-50 dark:bg-green-900/50 text-green-700 dark:text-green-300' : 'bg-red-50 dark:bg-red-900/50 text-red-700 dark:text-red-300'}`}>{c.status}</span>
                          </td>
                          <td className="px-4 py-2.5 text-brand-muted">{c.latencyMs != null ? `${c.latencyMs}ms` : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </section>
              </>
            )}
          </>
        )}
      </AsyncScreen>
    </div>
  )
}
